import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate, useParams } from 'react-router';

import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectQuery } from 'features/projects/api/projects';
import { isSubProjectDetail } from 'features/projects/ui/project-detail.types';
import {
  Armchair,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  FileText,
  Files,
  Handshake,
  Landmark,
  Laptop,
  type LucideIcon,
  Pencil,
  Plus,
  Receipt,
  Trash2,
} from 'lucide-react';
import { Collapsible } from 'radix-ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import {
  type MortgageEntityRequiredDocumentsRow,
  type MortgageRequiredDocumentSpec,
  useGetMortgageEntityRequiredDocumentsQuery,
  useGetMortgagePolicyEntitiesQuery,
  useUpdateMortgageEntityAdditionalDocumentsMutation,
} from '../api/mortgageEntities.api';
import useMortgageRules from '../hooks/useMortgageRules';
import MortgageAdditionalDocumentDialog from './MortgageAdditionalDocumentDialog';
import MortgageConfirmDialog from './MortgageConfirmDialog';

/* Raw backend value, no label map. */
const formatEmploymentType = (value: string): string => {
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const EMPLOYMENT_TYPE_ICONS: Record<string, LucideIcon> = {
  independiente: Laptop,
  pensionado: Armchair,
  renta_presunta: Receipt,
  informal: Handshake,
};

interface DocumentLine {
  spec: MortgageRequiredDocumentSpec;
  isBase: boolean;
  forMainBuyer: boolean;
  forComplement: boolean;
}

interface EmploymentTypeGroup {
  employmentType: string;
  lines: DocumentLine[];
}

/* Titular and complemento ask for the same documents almost always, so an identical spec becomes one line
   that records which profiles it applies to. */
const groupByEmploymentType = (rows: MortgageEntityRequiredDocumentsRow[]): EmploymentTypeGroup[] =>
  rows.reduce<EmploymentTypeGroup[]>((groups, row) => {
    const group = groups.find((item) => item.employmentType === row.employmentType) ?? {
      employmentType: row.employmentType,
      lines: [],
    };
    if (!groups.includes(group)) groups.push(group);

    const isMainBuyer = row.profile === 'mainBuyer';
    const specs = [
      ...row.base.map((spec) => ({ spec, isBase: true })),
      ...row.additional.map((spec) => ({ spec, isBase: false })),
    ];

    specs.forEach(({ spec, isBase }) => {
      const line =
        group.lines.find((item) => item.isBase === isBase && JSON.stringify(item.spec) === JSON.stringify(spec)) ??
        null;
      if (line) {
        line.forMainBuyer ||= isMainBuyer;
        line.forComplement ||= !isMainBuyer;
        return;
      }
      group.lines.push({ spec, isBase, forMainBuyer: isMainBuyer, forComplement: !isMainBuyer });
    });

    return groups;
  }, []);

const appliesToLabel = ({ forMainBuyer, forComplement }: DocumentLine): string => {
  if (forMainBuyer && forComplement) return 'Titular y complemento';
  return forMainBuyer ? 'Solo titular' : 'Solo complemento';
};

const documentDetails = (line: DocumentLine): string => {
  const { quantity, periodGranularity, validityDays } = line.spec;
  const quantityLabel =
    periodGranularity === 'month'
      ? `${quantity} ${quantity === 1 ? 'mes' : 'meses'}`
      : `${quantity} ${quantity === 1 ? 'archivo' : 'archivos'}`;
  const validityLabel = validityDays ? `Vigencia ${validityDays} días` : 'Sin vigencia';
  /* Base documents always gate the derivation; an entity document only when it is marked so. */
  const gateLabel = line.isBase || line.spec.isMinimumRequired ? 'Requerido para derivar' : null;
  return [quantityLabel, validityLabel, appliesToLabel(line), gateLabel].filter(Boolean).join(' · ');
};

interface EmploymentTypeCardProps {
  group: EmploymentTypeGroup;
  entityName: string;
  defaultOpen: boolean;
  onAdd: () => void;
  onEdit: (spec: MortgageRequiredDocumentSpec) => void;
  onDelete: (spec: MortgageRequiredDocumentSpec) => void;
}

const EmploymentTypeCard = ({
  group,
  entityName,
  defaultOpen,
  onAdd,
  onEdit,
  onDelete,
}: EmploymentTypeCardProps): ReactElement => {
  const entityCount = group.lines.filter((line) => !line.isBase).length;
  const EmploymentTypeIcon = EMPLOYMENT_TYPE_ICONS[group.employmentType] ?? BriefcaseBusiness;

  return (
    <Collapsible.Root
      defaultOpen={defaultOpen}
      className="group/card overflow-hidden rounded-xl border border-border-subtle bg-bg-surface shadow-sm transition-colors data-[state=open]:border-brand-vivid/25"
    >
      <Collapsible.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left outline-none transition-colors hover:bg-bg-surface-raised focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-vivid sm:gap-4 sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-bg-surface-alt text-text-subtle transition-colors group-data-[state=open]/card:bg-brand-vivid/10 group-data-[state=open]/card:text-brand-vivid">
              <EmploymentTypeIcon className="size-5" />
            </div>
            <div className="min-w-0">
              <h3 className="m-0 text-base font-semibold text-brand-navy font-display">
                {formatEmploymentType(group.employmentType)}
              </h3>
              <p className="m-0 mt-1 text-xs leading-relaxed text-text-subtle">
                {group.lines.length} {group.lines.length === 1 ? 'documento requerido' : 'documentos requeridos'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {entityCount > 0 && (
              <span className="hidden rounded-full bg-brand-vivid/8 px-2.5 py-1 text-xs font-medium text-brand-vivid sm:inline-flex">
                {entityCount} {entityCount === 1 ? 'adicional' : 'adicionales'}
              </span>
            )}
            <ChevronDown className="size-4 text-text-subtle transition-transform group-data-[state=open]/card:rotate-180" />
          </div>
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ul className="m-0 flex list-none flex-col divide-y divide-border-subtle border-t border-border-subtle px-4 sm:px-5">
          {group.lines.map((line) => (
            <li
              key={`${line.isBase ? 'base' : 'entity'}-${line.spec.documentType}`}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-4 sm:flex-nowrap"
            >
              <div className="flex min-w-0 flex-1 basis-full items-start gap-3 sm:basis-auto">
                <div
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-lg',
                    line.isBase ? 'bg-bg-surface-raised text-text-subtle' : 'bg-brand-vivid/8 text-brand-vivid'
                  )}
                >
                  <FileText className="size-4" />
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="m-0 text-sm font-medium break-words text-brand-navy">{line.spec.documentType}</p>
                  <p className="m-0 text-xs leading-relaxed text-text-subtle">{documentDetails(line)}</p>
                </div>
              </div>
              <div className="ml-12 flex min-w-0 items-center gap-3 sm:ml-0 sm:max-w-1/2">
                <span
                  className={cn(
                    'inline-flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium',
                    line.isBase ? 'bg-bg-surface-alt text-text-muted' : 'bg-brand-vivid/8 text-brand-vivid'
                  )}
                >
                  <span
                    className={cn('size-1.5 shrink-0 rounded-full', line.isBase ? 'bg-text-faint' : 'bg-brand-vivid')}
                  />
                  <span className="break-words">{line.isBase ? 'Base del país' : entityName}</span>
                </span>
                {!line.isBase && (
                  <div className="flex shrink-0 items-center gap-1 border-l border-border-subtle pl-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-text-subtle hover:bg-brand-vivid/8 hover:text-brand-vivid"
                      aria-label={`Editar ${line.spec.documentType}`}
                      title="Editar documento"
                      onClick={() => onEdit(line.spec)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-text-subtle hover:bg-error/8 hover:text-error"
                      aria-label={`Eliminar ${line.spec.documentType}`}
                      title="Eliminar documento"
                      onClick={() => onDelete(line.spec)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-3 border-t border-border-subtle bg-bg-surface-raised px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="m-0 text-xs font-medium text-brand-navy">Documentos adicionales</p>
            <p className="m-0 text-xs leading-relaxed text-text-subtle">Agrega requisitos propios de {entityName}.</p>
          </div>
          <Button type="button" onClick={onAdd} className="h-9 gap-2 px-3 shadow-sm">
            <Plus className="size-4" />
            Agregar documento
          </Button>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const MortgageDocumentsView = (): ReactElement => {
  const { masterProjectId } = useParams<{ masterProjectId: string }>();
  const projectId = Number(masterProjectId);

  const { data: project, isLoading, isError } = useGetProjectQuery(projectId, { skip: !masterProjectId });
  const {
    data: catalog = [],
    isLoading: isCatalogLoading,
    isError: isCatalogError,
  } = useGetMortgagePolicyEntitiesQuery();

  const isFinancialEntity = project?.isFinancialEntity ?? false;

  /* Only Lead Management uses this screen: its rules live on the master and on each of its products. */
  const ruleProjectIds = useMemo(
    () => (project ? [project.id, ...project.subProjects.map((subProject) => subProject.id)] : []),
    [project]
  );

  const { byProject, isLoading: areRulesLoading } = useMortgageRules(ruleProjectIds);

  /* Mirrors how the agent picks entities for a financial entity: its own financiers, active, same country,
     restricted to the projects' entityCodes (all of them when a project sets none). */
  const entities = useMemo(() => {
    if (!project) return [];
    const codesByProject = ruleProjectIds.map((id) => byProject.get(id)?.config.entityCodes ?? []);
    const acceptsAll = codesByProject.some((codes) => codes.length === 0);
    const codes = new Set(codesByProject.flat());

    return catalog.filter(
      (entity) =>
        entity.isActive &&
        entity.countryCode === project.countryCode &&
        entity.projectId === project.id &&
        (acceptsAll || codes.has(entity.code))
    );
  }, [project, catalog, ruleProjectIds, byProject]);

  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);

  const entity = entities.find((item) => item.id === selectedEntityId) ?? entities[0] ?? null;

  /* currentData is empty only while another entity loads: a refetch after saving keeps the cards and their
     open state on screen. */
  const { currentData: entityDocuments, isError: areDocumentsError } = useGetMortgageEntityRequiredDocumentsQuery(
    entity?.id ?? skipToken
  );

  const groups = useMemo(() => groupByEmploymentType(entityDocuments?.documents ?? []), [entityDocuments]);

  const baseDocumentTypes = useMemo(
    () =>
      (entityDocuments?.documents ?? []).reduce<Record<string, string[]>>((acc, row) => {
        acc[row.employmentType] = [...(acc[row.employmentType] ?? []), ...row.base.map((spec) => spec.documentType)];
        return acc;
      }, {}),
    [entityDocuments]
  );

  const [updateAdditionalDocuments, { isLoading: isSavingDocuments }] =
    useUpdateMortgageEntityAdditionalDocumentsMutation();
  const [editing, setEditing] = useState<{ employmentType: string; spec: MortgageRequiredDocumentSpec | null } | null>(
    null
  );
  const [deleting, setDeleting] = useState<MortgageRequiredDocumentSpec | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const additionalDocuments = entityDocuments?.additionalDocuments ?? [];

  /* The entity's additional documents are written as a whole list: every change is a new documentsConfig version. */
  const saveAdditionalDocuments = async (next: MortgageRequiredDocumentSpec[], onDone: () => void): Promise<void> => {
    if (!entity) return;
    setSaveError(null);
    try {
      await updateAdditionalDocuments({ entityId: entity.id, additionalDocuments: next }).unwrap();
      onDone();
    } catch (error) {
      setSaveError(extractErrorMessage(error, 'No se pudieron guardar los documentos de la entidad.'));
    }
  };

  const handleSave = (spec: MortgageRequiredDocumentSpec): void => {
    const original = editing?.spec;
    const next = original
      ? additionalDocuments.map((item) => (item.documentType === original.documentType ? spec : item))
      : [...additionalDocuments, spec];
    void saveAdditionalDocuments(next, () => setEditing(null));
  };

  const handleDelete = (): void => {
    if (!deleting) return;
    const next = additionalDocuments.filter((item) => item.documentType !== deleting.documentType);
    void saveAdditionalDocuments(next, () => setDeleting(null));
  };

  if (isLoading || isCatalogLoading || areRulesLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div className="h-16 bg-bg-surface-raised rounded-xl animate-pulse" />
        <div className="h-96 bg-bg-surface-raised rounded-xl animate-pulse" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
        No se pudo cargar la configuración hipotecaria de la inmobiliaria.
      </div>
    );
  }

  if (isSubProjectDetail(project)) {
    return <Navigate to={`/project/${project.masterProjectId}/mortgage/documents`} replace />;
  }

  if (!isFinancialEntity) {
    return <Navigate to={`/project/${project.id}/mortgage/config`} replace />;
  }

  return (
    <div className="flex flex-col gap-5">
      <header>
        <p className="text-xs font-bold uppercase tracking-widest text-brand-vivid mb-1 font-body">
          Documentos · Entidad financiera
        </p>
        <h2 className="text-2xl font-bold text-text-default m-0 font-display">{project.name}</h2>
        <span className="text-sm text-text-muted font-body">
          ID: {project.id} · {project.countryCode}
        </span>
      </header>

      {isCatalogError ? (
        <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
          No se pudo cargar el catálogo de entidades financieras. Puede que tu rol no tenga acceso a esa configuración.
        </div>
      ) : entities.length === 0 || !entity ? (
        <p className="rounded-md border border-border p-6 text-sm text-text-muted">
          Este proyecto no tiene entidades activas para evaluar a sus clientes.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-bg-surface p-4 sm:p-5">
            <Tabs value={String(entity.id)} onValueChange={(value) => setSelectedEntityId(Number(value))}>
              <TabsList
                aria-label="Entidad financiera"
                className="h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0 group-data-horizontal/tabs:h-auto"
              >
                {entities.map((item) => (
                  <TabsTrigger
                    key={item.id}
                    value={String(item.id)}
                    className="group/entity h-9 flex-none gap-2 rounded-full border border-border-subtle bg-bg-surface px-3.5 text-sm font-medium text-brand-navy transition-colors after:hidden hover:bg-bg-surface-raised data-[state=active]:border-brand-vivid/25 data-[state=active]:bg-brand-vivid/5 data-[state=active]:shadow-none"
                  >
                    <Landmark className="size-4 text-text-subtle transition-colors group-data-[state=active]/entity:text-brand-vivid" />
                    {item.name}
                    <Check className="hidden size-4 text-brand-vivid group-data-[state=active]/entity:block" />
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-start gap-2.5">
              <Files className="mt-0.5 size-4 shrink-0 text-brand-vivid" />
              <p className="m-0 text-sm leading-relaxed text-text-muted">
                Documentos que se solicitan a los clientes de{' '}
                <span className="font-medium text-brand-navy">{entity.name}</span>. Incluye la base del país y los
                requisitos adicionales de la entidad.
              </p>
            </div>
          </div>

          {saveError && !editing && (
            <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
              {saveError}
            </div>
          )}

          {areDocumentsError ? (
            <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
              No se pudieron cargar los documentos de la entidad.
            </div>
          ) : !entityDocuments ? (
            <div className="h-96 bg-bg-surface-raised rounded-xl animate-pulse" />
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map((group, index) => (
                <EmploymentTypeCard
                  key={group.employmentType}
                  group={group}
                  entityName={entity.name}
                  defaultOpen={index === 0}
                  onAdd={() => {
                    setSaveError(null);
                    setEditing({ employmentType: group.employmentType, spec: null });
                  }}
                  onEdit={(spec) => {
                    setSaveError(null);
                    setEditing({ employmentType: group.employmentType, spec });
                  }}
                  onDelete={(spec) => {
                    setSaveError(null);
                    setDeleting(spec);
                  }}
                />
              ))}
            </div>
          )}

          {editing && (
            <MortgageAdditionalDocumentDialog
              spec={editing.spec}
              employmentType={editing.employmentType}
              employmentTypeOptions={groups.map((group) => group.employmentType)}
              baseDocumentTypes={baseDocumentTypes}
              entityName={entity.name}
              takenDocumentTypes={additionalDocuments
                .map((item) => item.documentType)
                .filter((name) => name !== editing.spec?.documentType)}
              isSaving={isSavingDocuments}
              saveError={saveError}
              onCancel={() => setEditing(null)}
              onSave={handleSave}
            />
          )}

          <MortgageConfirmDialog
            copy={
              deleting && {
                title: 'Eliminar documento',
                description: `${entity.name} dejará de pedir "${deleting.documentType}" a los clientes que la acepten desde ahora.${
                  (deleting.employmentTypes?.length ?? 0) !== 1
                    ? ' Aplica a más de un tipo de empleo, así que se quita de todos.'
                    : ''
                }`,
                confirmLabel: 'Eliminar',
              }
            }
            isLoading={isSavingDocuments}
            onCancel={() => setDeleting(null)}
            onConfirm={handleDelete}
          />
        </>
      )}
    </div>
  );
};

export default MortgageDocumentsView;
